import React from 'react';

import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Fab, Paper, Typography } from '@mui/material';
import { Delete } from '@mui/icons-material';
import ThemedTooltip from '@acnb/CustomComponents/Tooltip/ThemedTooltip';

import { useLanguageReference } from 'services/Hooks/useLanguageReference';
import { getMerchandisingProgramItemSubLabel, shouldShowQuantityPerPackageInfo } from 'services/Utilities/Utilities';
import { useMerchandisingProgramQuoteSortedSeason } from 'services/Data/MerchandisingProgramQuoteService';
import useImage from 'services/Hooks/useImage';

const useStyles = makeStyles(() => ({
  squareFab: {
    borderRadius: 0,
    width: 36,
    height: 36,
    minHeight: 36,
  },
}));

const MerchandisingProgramQuoteItemCard = ({ item, handleOpenDeleteItemModal }) => {
  const classes = useStyles();

  const { t } = useTranslation();
  const { labelReference } = useLanguageReference();
  const { getMerchandisingProgramImage } = useImage();
  const { data: sortedMerchandisingProgramSeasonEnums } = useMerchandisingProgramQuoteSortedSeason();
  const subLabel = getMerchandisingProgramItemSubLabel(item, labelReference, sortedMerchandisingProgramSeasonEnums);
  const imageSrc =
    item?.imgSrc && item?.merchandisingProgram_Id
      ? getMerchandisingProgramImage(item.merchandisingProgram_Id, item.imgSrc.id)
      : '/images/brp-logo.png';
  const quantity = Number(item?.quantity ?? 0);
  const unitPrice = Number(item?.unitPrice ?? 0);
  const promoUnitPrice = Number(item?.unitPricePromotion ?? item?.unitPrice ?? 0);
  const hasPromotion = promoUnitPrice !== unitPrice;
  const subtotal = Number(item?.subTotal ?? promoUnitPrice * quantity);
  const currency = item?.currency || item?.currencyName || '';

  return (
    <Paper elevation={3} sx={{ width: '100%', borderRadius: 3, overflow: 'hidden', mb: 4 }}>
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'row',
          alignItems: 'center',
          minHeight: 130,
        }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            px: 2,
            py: 1.5,
            width: 240,
            flexShrink: 0,
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 44, flexShrink: 0 }}>
            <ThemedTooltip title={t('removeFromCart')} placement="top">
              <Fab color="error" size="small" className={classes.squareFab} onClick={() => handleOpenDeleteItemModal(item)}>
                <Delete />
              </Fab>
            </ThemedTooltip>
          </Box>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', height: 160 }}>
            <Box
              component="img"
              src={imageSrc}
              alt={item?.name}
              sx={{ maxWidth: '100%', maxHeight: 150, objectFit: 'contain' }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            maxWidth: { md: 360, lg: 460 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            px: 2.5,
            py: 1.5,
          }}>
          <Typography fontWeight={700} sx={{ fontSize: 20, lineHeight: 1.2, mb: 0.25 }}>
            {item?.name}
          </Typography>
          <Typography sx={{ fontSize: 15, color: 'text.secondary' }}>{subLabel}</Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            justifyContent: 'center',
            px: 3,
            py: 1.5,
            width: 180,
            flexShrink: 0,
            ml: 'auto',
            borderLeft: theme => `1px solid ${theme.palette.divider}`,
            alignSelf: 'stretch',
          }}>
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {t('unitPrice')}
          </Typography>
          {shouldShowQuantityPerPackageInfo(item.quantityPerPackage) && (
            <Typography fontSize={11} fontWeight="bold" color="text.secondary" sx={{ mb: 0.5, textAlign: 'right' }}>
              {t('quantityPerPackageInfo', { quantityPerPackage: item.quantityPerPackage })}
            </Typography>
          )}
          {hasPromotion && (
            <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
              ${unitPrice.toFixed(2)}
            </Typography>
          )}
          <Typography
            fontWeight={700}
            sx={{ fontSize: 18, whiteSpace: 'nowrap' }}
            color={hasPromotion ? 'error' : 'text.primary'}>
            ${promoUnitPrice.toFixed(2)} {currency}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2.5,
            py: 1.5,
            width: 175,
            flexShrink: 0,
            borderLeft: theme => `1px solid ${theme.palette.divider}`,
            alignSelf: 'stretch',
          }}>
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {t('quantity')}
          </Typography>
          <Typography fontWeight={700} sx={{ fontSize: 20, lineHeight: 1.2 }}>
            {quantity}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            justifyContent: 'center',
            px: 3,
            py: 1.5,
            width: 190,
            flexShrink: 0,
            borderLeft: theme => `1px solid ${theme.palette.divider}`,
            alignSelf: 'stretch',
          }}>
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {t('total')}
          </Typography>
          {hasPromotion && (
            <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
              ${(unitPrice * quantity).toFixed(2)}
            </Typography>
          )}
          <Typography
            fontWeight={800}
            sx={{ fontSize: 22, lineHeight: 1.1 }}
            color={hasPromotion ? 'error' : 'text.primary'}>
            ${subtotal.toFixed(2)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {currency}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          flexDirection: 'column',
          p: 2,
          gap: 1.5,
        }}>
        <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', mb: 0.5 }}>
          <Box
            component="img"
            src={imageSrc}
            alt={item?.name}
            sx={{ maxWidth: '100%', maxHeight: 170, objectFit: 'contain' }}
          />
          <ThemedTooltip title={t('removeFromCart')} placement="top">
            <Fab
              color="error"
              size="small"
              className={classes.squareFab}
              sx={{ position: 'absolute', top: 0, right: 0 }}
              onClick={() => handleOpenDeleteItemModal(item)}>
              <Delete />
            </Fab>
          </ThemedTooltip>
        </Box>

        <Box>
          <Typography fontWeight={700} sx={{ fontSize: 20, lineHeight: 1.2 }}>
            {item?.name}
          </Typography>
          <Typography sx={{ fontSize: 18, color: 'text.secondary', mt: 0.25 }}>{subLabel}</Typography>
        </Box>

        <Divider />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <Box>
            <Typography fontWeight={700} sx={{ fontSize: 15 }}>
              {t('unitPrice')}
            </Typography>
            {shouldShowQuantityPerPackageInfo(item.quantityPerPackage) && (
              <Typography fontSize={12} fontWeight="bold" color="text.secondary">
                {t('quantityPerPackageInfo', { quantityPerPackage: item.quantityPerPackage })}
              </Typography>
            )}
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            {hasPromotion && (
              <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                ${unitPrice.toFixed(2)} {currency}
              </Typography>
            )}
            <Typography fontWeight={700} sx={{ fontSize: 18 }} color={hasPromotion ? 'error' : 'text.primary'}>
              ${promoUnitPrice.toFixed(2)} {currency}
            </Typography>
          </Box>
        </Box>

        <Divider />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography fontWeight={700} sx={{ fontSize: 15 }}>
            {t('quantity')}
          </Typography>
          <Typography fontWeight={700} sx={{ fontSize: 18 }}>
            {quantity}
          </Typography>
        </Box>

        <Divider />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', pb: 0.5 }}>
          <Typography fontWeight={700} sx={{ fontSize: 15 }}>
            {t('price')}
          </Typography>
          <Box sx={{ textAlign: 'right' }}>
            {hasPromotion && (
              <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                ${(unitPrice * quantity).toFixed(2)} {currency}
              </Typography>
            )}
            <Typography
              fontWeight={800}
              sx={{ fontSize: 24, lineHeight: 1.1 }}
              color={hasPromotion ? 'error' : 'text.primary'}>
              ${subtotal.toFixed(2)} {currency}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default MerchandisingProgramQuoteItemCard;
